import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { provider, signer, connectionError, switchToTestWallet, getCurrentTestWallet, getAllTestWallets, ethersUtils, createContract, getAccounts, initializeEthers, validateNetwork, validateSigner, walletManager } from './EthersSetup.js';
import GameMakerConfig from '../deployedContractsInfo/GameMaker.json';
import GameRegistryConfig from '../deployedContractsInfo/GameRegistry.json';
import contractAddresses from '../deployedContractsInfo/contract-address.json';
import { useGameAddresses } from './GameInterface.js';
import OneOnOneGamesSection from './OneOnOneGamesSection';
import WalletSelection from './basics/WalletSelection';
import TopRightBar from './basics/TopRightBar';
import FastTravelNavigation from './FastTravelNavigation';
import AsymmetricGamesSection from './AsymmetricGamesSection';
import BlockchainErrorOverlay from './BlockchainErrorOverlay';
import { BlockchainStateProvider, useBlockchainState } from '../contexts/BlockchainStateContext.js';
import '../index.css';
import LazyMagpieImage from '../pictures/lazymagpie2.png';

/**
 * DappInner Component - Refactored with New Architecture
 *
 * Simplified Dapp component that focuses on routing and wallet management.
 * All blockchain data management is now handled by the BlockchainStateContext.
 *
 * Key improvements:
 * - Removed infinite render loop debugging code
 * - Eliminated complex blockchain data management
 * - Simplified prop drilling
 * - Clean separation of concerns
 */
const DappInner = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState([]);
  const [queriedGames, setQueriedGames] = useState([]);
  const [isTestMode, setIsTestMode] = useState(false);
  const [showWalletSelection, setShowWalletSelection] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [globalError, setGlobalError] = useState(null);
  const [walletInitialized, setWalletInitialized] = useState(false);

  // Get blockchain state management
  const {
    initialize: initializeBlockchainState,
    isInitialized: blockchainInitialized,
    isInitializing: blockchainInitializing,
    error: blockchainError,
    reset: resetBlockchainState,
    // Transaction functions
    makeOffer: makeOfferFromContext,
    acceptOffer,
    killGame,
    offerDraw,
    acceptDrawOffer,
    withdrawDrawOffer
  } = useBlockchainState();

  // Wrapper function to match MakeOfferForm's expected signature
  const makeOffer = useCallback(async (game, player1Bet, player2Bet, timeout, gameParams, targetAddress) => {
    // Convert gameParams from string to array of numbers (smart contract expects uint256[] array)
    let gameParamsArray;
    if (typeof gameParams === 'string' && gameParams !== '') {
      // Convert single parameter string to array of numbers
      gameParamsArray = [parseInt(gameParams, 10)];
    } else if (Array.isArray(gameParams)) {
      // Already an array, ensure all elements are numbers
      gameParamsArray = gameParams.map(param => parseInt(param, 10));
    } else {
      // Empty or invalid, use empty array
      gameParamsArray = [];
    }

    // Convert empty targetAddress to zero address (prevents ENS resolution error)
    const validTargetAddress = targetAddress && targetAddress.trim() !== ''
      ? targetAddress
      : '0x0000000000000000000000000000000000000000';

    // Convert bet amounts from ether strings to wei BigNumbers (smart contract expects wei)
    const player1BetWei = ethersUtils.parseEther(player1Bet.toString());
    const player2BetWei = ethersUtils.parseEther(player2Bet.toString());

    console.log('🎯 makeOffer wrapper: Converting parameters:', {
      gameParams: { original: gameParams, converted: gameParamsArray },
      targetAddress: { original: targetAddress, converted: validTargetAddress },
      bets: {
        player1: { original: player1Bet, converted: player1BetWei.toString() },
        player2: { original: player2Bet, converted: player2BetWei.toString() }
      }
    });

    // Convert game object to gameName and call the context function with correct parameter order
    return makeOfferFromContext(game.name, player2BetWei, timeout, gameParamsArray, validTargetAddress, player1BetWei);
  }, [makeOfferFromContext]);

  // Global error handler for blockchain operations
  const showGlobalError = useCallback((error) => {
    console.error('Global blockchain error:', error);
    setGlobalError(error);
  }, []);

  const dismissGlobalError = useCallback(() => {
    setGlobalError(null);
  }, []);

  // Determine activeTab and selectedGame from URL
  const getActiveTabFromPath = () => {
    if (location.pathname === '/' || location.pathname === '/information') {
      return 'information';
    } else if (location.pathname.startsWith('/1on1')) {
      return 'oneOnOne';
    } else if (location.pathname.startsWith('/asymmetric')) {
      return 'asymmetric';
    } else if (location.pathname.startsWith('/blog')) {
      return 'blog';
    }
    return 'information';
  };

  const activeTab = getActiveTabFromPath();
  const selectedGame = params.gameName || null;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(() => {
    const saved = localStorage.getItem('gamemaker_disclaimer_accepted');
    return saved === 'true';
  });
  const [isDisclaimerCollapsed, setIsDisclaimerCollapsed] = useState(() => {
    const saved = localStorage.getItem('gamemaker_disclaimer_collapsed');
    const accepted = localStorage.getItem('gamemaker_disclaimer_accepted') === 'true';
    if (accepted && saved === null) {
      return true;
    }
    return saved === 'true';
  });

  const [gameUrls, setGameUrls] = useState({});

  // Use dynamic game address loading
  const { gameAddresses, loading: gameAddressesLoading, error: gameAddressesError } = useGameAddresses(
    provider,
    GameRegistryConfig,
    contractAddresses.GameRegistry
  );

  // Save tab preference to localStorage
  useEffect(() => {
    localStorage.setItem('gamemaker_active_tab', activeTab);
  }, [activeTab]);

  // Save disclaimer acceptance to localStorage
  useEffect(() => {
    localStorage.setItem('gamemaker_disclaimer_accepted', disclaimerAccepted.toString());

    if (!disclaimerAccepted && activeTab !== 'information') {
      navigate('/information');
    }

    if (!disclaimerAccepted) {
      setIsDisclaimerCollapsed(false);
    }
  }, [disclaimerAccepted, activeTab, navigate]);

  // Save disclaimer collapse state to localStorage
  useEffect(() => {
    localStorage.setItem('gamemaker_disclaimer_collapsed', isDisclaimerCollapsed.toString());
  }, [isDisclaimerCollapsed]);

  // Helper function to get tab display info
  const getTabInfo = (tab) => {
    const tabMap = {
      'information': { label: 'ℹ️ Information', icon: 'ℹ️' },
      'oneOnOne': { label: '🎯 1:1 Games', icon: '🎯' },
      'asymmetric': { label: '🎰 Asymmetric Games', icon: '🎰' },
      'blog': { label: '📝 Blog', icon: '📝' }
    };
    return tabMap[tab] || { label: 'Select Tab', icon: '📋' };
  };

  // Handle disclaimer acceptance
  const handleDisclaimerAcceptance = (accepted) => {
    setDisclaimerAccepted(accepted);
  };

  // Handle disclaimer collapse toggle
  const handleDisclaimerToggle = () => {
    if (disclaimerAccepted) {
      setIsDisclaimerCollapsed(!isDisclaimerCollapsed);
    }
  };

  // Handle dropdown tab selection
  const handleTabSelection = (newTab) => {
    if (!disclaimerAccepted && newTab !== 'information' && newTab !== 'blog') {
      alert('Please accept the disclaimer on the Information page before accessing other sections.');
      return;
    }

    switch (newTab) {
      case 'information':
        navigate('/information');
        break;
      case 'oneOnOne':
        navigate('/1on1');
        break;
      case 'asymmetric':
        navigate('/asymmetric');
        break;
      case 'blog':
        navigate('/blog');
        break;
      default:
        navigate('/information');
    }

    setIsDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.navigation-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Initialize blockchain state when wallet is connected
  useEffect(() => {
    const initializeBlockchain = async () => {
      if (!provider || !accounts[0] || !contractAddresses.GameMaker || !GameMakerConfig.abi) {
        return;
      }

      // Skip blockchain initialization for information and blog tabs
      if (activeTab === 'information' || activeTab === 'blog') {
        return;
      }

      console.log('🔧 Initializing blockchain state...');

      try {
        const success = await initializeBlockchainState({
          gameMakerAddress: contractAddresses.GameMaker,
          gameMakerAbi: GameMakerConfig.abi,
          gameAddresses: gameAddresses || {},
          playerAccount: accounts[0],
          provider,
          createContract,
          ethersUtils
        });

        if (success) {
          console.log('✅ Blockchain state initialized successfully');
        }
      } catch (error) {
        console.error('❌ Failed to initialize blockchain state:', error);
        showGlobalError(error);
      }
    };

    initializeBlockchain();
  }, [provider, accounts, gameAddresses, contractAddresses.GameMaker, activeTab, initializeBlockchainState]);

  // Check for test mode
  const urlParams = new URLSearchParams(window.location.search);
  const testModeFromUrl = urlParams.get('test') === 'true';

  useEffect(() => {
    setIsTestMode(testModeFromUrl);

    if (testModeFromUrl) {
      window.TEST_MODE = true;
      window.PLAYWRIGHT_TESTING = true;
      console.log('🧪 Test mode enabled via URL parameter');

      // Expose contract artifacts globally for Playwright tests
      window.contractAddresses = contractAddresses;
      window.GameMakerConfig = GameMakerConfig;
      window.GameRegistryConfig = GameRegistryConfig;
      window.createContract = createContract;
      window.initializeEthers = initializeEthers;
      window.getAccounts = getAccounts;
      window.signer = signer;

      // Expose test wallets globally for Playwright compatibility
      const testWallets = getAllTestWallets();
      if (testWallets && testWallets.length > 0) {
        window.testWallets = testWallets;
        window.testWalletManager = {
          currentWalletIndex: 0,
          initialize: (wallets) => {
            console.log('🧪 TestWalletManager initialized with', wallets ? wallets.length : 0, 'wallets');
          },
          switchWallet: (index) => {
            if (index >= 0 && index < testWallets.length) {
              switchToTestWallet(index);
              window.testWalletManager.currentWalletIndex = index;
              console.log('🧪 Switched to wallet', index);
            }
          },
          getCurrentWallet: () => getCurrentTestWallet()
        };
      }
    }

    const init = async () => {
      if (testModeFromUrl) {
        console.log('🧪 Test mode - skipping wallet initialization');
        setIsInitializing(false);
        setWalletInitialized(true);
        return;
      }

      if (activeTab === 'information' || activeTab === 'blog') {
        console.log('📄 Information/Blog tab - skipping wallet operations');
        setIsInitializing(false);
        setShowWalletSelection(false);
        setAccounts([]);
        return;
      }

      if (walletInitialized && (activeTab === 'oneOnOne' || activeTab === 'asymmetric')) {
        console.log('🔗 Wallet already initialized for game tabs, skipping...');
        setIsInitializing(false);
        return;
      }

      try {
        console.log('🔗 Initializing wallet for game tab:', activeTab);
        await initializeEthers();

        const accounts = await getAccounts();
        if (accounts.length === 0) {
          console.log('No wallet connected - showing wallet selection');
          setShowWalletSelection(true);
          setIsInitializing(false);
          setWalletInitialized(true);
          return;
        }

        setAccounts(accounts);
        setShowWalletSelection(false);
        setIsInitializing(false);
        setWalletInitialized(true);
        console.log('Accounts loaded:', accounts);
      } catch (error) {
        console.error('Error initializing app:', error);
        if (activeTab === 'oneOnOne' || activeTab === 'asymmetric') {
          setShowWalletSelection(true);
        }
        setIsInitializing(false);
        setWalletInitialized(true);
      }
    };
    init();
  }, [testModeFromUrl, activeTab, walletInitialized]);

  // Wallet connection handlers
  const handleWalletConnected = async () => {
    try {
      const accounts = await getAccounts();
      setAccounts(accounts);
      setShowWalletSelection(false);
      setIsInitializing(false);
      setWalletInitialized(true);
      console.log('Wallet connected successfully:', accounts);
    } catch (error) {
      console.error('Error after wallet connection:', error);
    }
  };

  const handleWalletError = (error) => {
    console.error('Wallet connection error:', error);
  };

  const handleWalletDisconnect = async () => {
    try {
      setAccounts([]);
      setShowWalletSelection(true);
      setWalletInitialized(false);
      resetBlockchainState(); // Clear blockchain state when wallet disconnects
      console.log('Wallet disconnected, blockchain state reset');
    } catch (error) {
      console.error('Error after wallet disconnect:', error);
      setShowWalletSelection(true);
      setWalletInitialized(false);
    }
  };

  // Show wallet selection if needed
  if (showWalletSelection && !testModeFromUrl && (activeTab === 'oneOnOne' || activeTab === 'asymmetric')) {
    return (
      <WalletSelection
        onWalletConnected={handleWalletConnected}
        onError={handleWalletError}
      />
    );
  }

  return (
    <div className="container">
      {/* Header Section - Logo, Navigation Dropdown, Top Right Bar */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <img src={LazyMagpieImage} alt="LazyMagpie Logo" className="w-16 h-16 md:w-20 md:h-20 object-cover" />
          <div className="flex flex-col">
            <h1 className="text-xl md:text-2xl font-bold text-left">LazyMagpie</h1>
            <p className="text-xs md:text-sm text-left text-gray-600">
              Welcome to Lazymagpie's GameMaker! A place to offer and accept smartcontracts.
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Navigation Dropdown */}
          <div className="relative navigation-dropdown">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200 font-medium text-sm"
            >
              <span>{getTabInfo(activeTab).label}</span>
              <svg className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                <div className="py-1">
                  <button
                    onClick={() => handleTabSelection('information')}
                    className={`w-full text-left px-4 py-2 text-sm transition ${
                      activeTab === 'information'
                        ? 'bg-blue-600 text-white font-medium'
                        : '!bg-white !text-gray-900 hover:!bg-gray-100 hover:!text-gray-900'
                    }`}
                  >
                    ℹ️ Information
                  </button>
                  <button
                    onClick={() => handleTabSelection('oneOnOne')}
                    className={`w-full text-left px-4 py-2 text-sm transition ${
                      activeTab === 'oneOnOne'
                        ? 'bg-blue-600 text-white font-medium'
                        : disclaimerAccepted
                          ? '!bg-white !text-gray-900 hover:!bg-gray-100 hover:!text-gray-900'
                          : '!bg-gray-100 !text-gray-400 cursor-not-allowed'
                    }`}
                    disabled={!disclaimerAccepted}
                  >
                    🎯 1:1 Games {!disclaimerAccepted && '🔒'}
                  </button>
                  <button
                    onClick={() => handleTabSelection('asymmetric')}
                    className={`w-full text-left px-4 py-2 text-sm transition ${
                      activeTab === 'asymmetric'
                        ? 'bg-blue-600 text-white font-medium'
                        : disclaimerAccepted
                          ? '!bg-white !text-gray-900 hover:!bg-gray-100 hover:!text-gray-900'
                          : '!bg-gray-100 !text-gray-400 cursor-not-allowed'
                    }`}
                    disabled={!disclaimerAccepted}
                  >
                    🎰 Asymmetric Games {!disclaimerAccepted && '🔒'}
                  </button>
                  <button
                    onClick={() => handleTabSelection('blog')}
                    className={`w-full text-left px-4 py-2 text-sm transition ${
                      activeTab === 'blog'
                        ? 'bg-blue-600 text-white font-medium'
                        : '!bg-white !text-gray-900 hover:!bg-gray-100 hover:!text-gray-900'
                    }`}
                  >
                    📝 Blog
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Top Right Bar with Wallet and Address Book */}
          <TopRightBar
            onWalletDisconnect={handleWalletDisconnect}
            showInTestMode={true}
          />
        </div>
      </div>

      {/* Test Mode UI */}
      {isTestMode && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-6 test-wallet-switcher" data-testid="test-mode-indicator">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="bg-yellow-500 text-white px-2 py-1 rounded text-xs font-bold test-mode-indicator">
                🧪 TEST MODE
              </div>
              <span className="text-sm text-gray-600">
                Current wallet: {getCurrentTestWallet()?.name || 'Loading...'}
              </span>
              <span className="text-xs text-gray-500 font-mono">
                ({getCurrentTestWallet()?.address || ''})
              </span>
            </div>
            <div className="flex space-x-2">
              {getAllTestWallets().map((wallet, index) => (
                <button
                  key={wallet.address}
                  onClick={() => switchToTestWallet(index)}
                  className={`px-3 py-1 text-xs rounded ${
                    getCurrentTestWallet()?.address === wallet.address
                      ? 'bg-blue-500 text-white font-bold'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {wallet.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {connectionError && (
        <>
          <h1 className="text-2xl font-bold text-center text-red-600">Error</h1>
          <p className="text-center text-red-600">{connectionError}</p>
        </>
      )}

      {isInitializing && !connectionError && (
        <div className="text-center py-8">
          <h1 className="text-2xl font-bold text-gray-600 mb-2">Initializing...</h1>
          <p className="text-gray-500">Checking wallet connection...</p>
        </div>
      )}

      {!connectionError && !isInitializing && (
        <div className="mt-6">
          {/* Tab Content */}
          {activeTab === 'information' && (
            <div className="space-y-8">
              {/* Disclaimer Section */}
              <div className="bg-white border border-gray-300 rounded-lg p-6 mb-8">
                <div className="mb-6">
                  <div
                    className={`flex justify-between items-center mb-4 ${disclaimerAccepted ? 'cursor-pointer' : ''}`}
                    onClick={handleDisclaimerToggle}
                  >
                    <h2 className="text-2xl font-bold text-gray-800 text-left">Readme!</h2>
                    {disclaimerAccepted && (
                      <div className="text-sm text-gray-500">
                        {isDisclaimerCollapsed ? '+' : '-'}
                      </div>
                    )}
                  </div>

                  <p className="text-left text-gray-800 mb-4">This is a side project of a single dev, who is not even a developer in his main job, please treat it as such.</p>

                  {!(disclaimerAccepted && isDisclaimerCollapsed) && (
                    <div className="text-gray-800 space-y-4 text-left">
                      <p className="font-bold text-gray-800 mb-2 text-left"><strong>1.</strong> Do not rely on professional support, bug free code or anything else.</p>
                      <p className="mb-2 text-left"><strong>2. Only put in as much value as you are willing to lose</strong>, be it due to bad luck, misunderstanding, being robbed or bugs. (If you don't want to risk real value, you can ask if we have a testnet deployment, or just look for games with 0 bet)</p>
                      <p className="mb-2 text-left"><strong>3. Read the code</strong> - the underlying smartcontract code is verified on platforms like polyscan. I strongly recommend to READ THE CODE, and point out flaws in a friendly manner so they can be fixed.</p>
                      <p className="text-left">If you have done 3. you will already be aware, but just to point out examples of shortcoming I expect you to be aware of (or at least not act surprised about):</p>
                      <p className="text-left"><strong>4.</strong> Examples of shortcomings:</p>
                      <p className="text-left"><strong>4a.</strong> we dont use real randomness, we use cheap randomness. the randomness could be tampered with. if the incentive is big enough it will be tampered with (so glimpse at 2.)</p>
                      <p className="text-left"><strong>4b.</strong> the games are not necessarily balanced and fair for every offered constellation.</p>
                      <p className="text-left"><strong>4c.</strong> blockchain cannot be reverted (at least not by me). Once your currency is gone it is unlikely anyone can help you get it back.</p>
                      <p className="text-left"><strong>4d.</strong> I recommend to only connect a wallet with limited funds (see 2.) to avoid any misclicks, misunderstandings, bugs or even malicious players being able to seriously hurt you.</p>
                    </div>
                  )}
                </div>

                <div className="bg-yellow-50 border border-yellow-300 rounded p-4">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="disclaimer-checkbox"
                      checked={disclaimerAccepted}
                      onChange={(e) => handleDisclaimerAcceptance(e.target.checked)}
                      className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="disclaimer-checkbox" className="text-sm text-gray-700 cursor-pointer text-left">
                      <strong>I accept this. I should read the code and not put in more than I am willing to lose. I will not be a sore loser and complain. I will not expect anyone but myself to prevent me from mistakes.</strong>
                    </label>
                  </div>
                  {!disclaimerAccepted && (
                    <p className="text-xs text-red-600 mt-2 ml-8 text-left">
                      If you dont feel comfortable with this, that is perfectly fine. Then this site and functionality is not for you. Go away now.
                    </p>
                  )}
                </div>
              </div>

              {/* Discord/Blog Boxes */}
              <div className="grid md:grid-cols-2 gap-6 mb-8 max-w-4xl mx-auto">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                  <div className="mb-4">
                    <a
                      href="https://discord.gg/U8kzVujUkU"
                      className="inline-block bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors font-medium"
                    >
                      💬 Join on Discord
                    </a>
                  </div>
                  <p className="text-sm text-blue-600 mb-2">If Discord doesn't work, here is the mail, but don't expect it to be read every week:</p>
                  <p className="text-xs text-blue-500">the lazy magpie 137 - at - google's mail domain .com (with dots instead of space before the "at")</p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <div className="mb-4">
                    <Link
                      to="/blog"
                      className="inline-block bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors font-medium"
                    >
                      📝 Have a look at the blogs
                    </Link>
                  </div>
                  <p className="text-sm text-green-600 mb-2">Learn more about the platform, games, and development updates</p>
                  <p className="text-xs text-green-500">Get insights into game mechanics, strategy tips, and project roadmap</p>
                </div>
              </div>

              {/* LazyMagpie TLDR Box */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
                <h3 className="text-2xl font-semibold text-gray-800 mb-6 text-center">LazyMagpie TLDR</h3>

                <div className="text-center mb-8">
                  <p className="text-lg text-gray-600 mb-4">
                    A decentralized gaming platform for competitive and assymetric contracts on the blockchain.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg border border-gray-200 mb-8">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">🔧 How It Works</h4>
                  <div className="text-sm text-gray-600 space-y-2">
                    <p><strong>1. Connect Wallet:</strong> Use MetaMask or input a private key to get started</p>
                    <p><strong>2. Choose Game Type:</strong> Select 1:1 competitive or asymmetric contracts</p>
                    <p><strong>3. Make or Accept Offers:</strong> Create new game offers or join existing ones</p>
                    <p><strong>4. Play & Win:</strong> Games execute on-chain with automatic payouts to winners</p>
                  </div>
                </div>

                <div className="text-center mb-8">
                  <p className="text-lg font-medium text-gray-700 mb-6">
                    Select "🎯 1:1 Games" or "🎰 Asymmetric Games" from the dropdown above to start playing!
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                    <div className="mb-3 text-center">
                      <Link
                        to="/1on1"
                        className="inline-block bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors font-medium"
                      >
                        🎯 1:1 Competitive Games
                      </Link>
                    </div>
                    <p className="text-blue-700 mb-4">
                      Challenge other players in fair, skill-based games where both players stake equal amounts.
                    </p>
                    <ul className="text-sm text-blue-600 space-y-2">
                      <li>• <strong>TicTacToe:</strong> Classic 3x3 grid strategy</li>
                      <li>• <strong>Rock Paper Scissors:</strong> Hidden commitment with cryptographic reveals</li>
                      <li>• <strong>CoinFlip:</strong> Simple luck-based instant resolution</li>
                      <li>• <strong>Risk:</strong> Strategic territory conquest with dice combat</li>
                      <li>• <strong>Mill:</strong> Traditional Nine Men's Morris board game</li>
                    </ul>
                  </div>

                  <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                    <div className="mb-3 text-center">
                      <Link
                        to="/asymmetric"
                        className="inline-block bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors font-medium"
                      >
                        🎰 Asymmetric Games
                      </Link>
                    </div>
                    <p className="text-purple-700 mb-4">
                      Play against the house or participate in decentralized experiences with varying stakes.
                    </p>
                    <ul className="text-sm text-purple-600 space-y-2">
                      <li>• <strong>DICY:</strong> Bet on outcome of random variable against the bank</li>
                      <li>• <strong>Investment System:</strong> Become a house investor and earn shares</li>
                      <li>• <strong>Flexible Stakes:</strong> Bet amounts based on house bankroll limits</li>
                      <li>• <strong>Token System:</strong> ERC20 shares for profit distribution</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'oneOnOne' && (
            <OneOnOneGamesSection
              queriedGames={queriedGames}
              setQueriedGames={setQueriedGames}
              GameRegistryConfig={GameRegistryConfig}
              GameRegistryContractAddress={contractAddresses.GameRegistry}
              provider={provider}
              signer={signer}
              ethersUtils={ethersUtils}
              createContract={createContract}
              makeOffer={makeOffer}
              acceptOffer={acceptOffer}
              killGame={killGame}
              offerDraw={offerDraw}
              acceptDrawOffer={acceptDrawOffer}
              withdrawDrawOffer={withdrawDrawOffer}
              gameUrls={gameUrls}
              setGameUrls={setGameUrls}
              validateNetwork={validateNetwork}
              validateSigner={validateSigner}
              playerAccount={accounts[0]}
              games={queriedGames}
            />
          )}

          {activeTab === 'asymmetric' && (
            <AsymmetricGamesSection />
          )}
        </div>
      )}

      {/* Fast Travel Navigation - only show on game tabs */}
      {(activeTab === 'oneOnOne' || activeTab === 'asymmetric') && (
        <FastTravelNavigation activeTab={activeTab} />
      )}

      {/* Global Blockchain Error Overlay */}
      <BlockchainErrorOverlay error={globalError} onDismiss={dismissGlobalError} />
    </div>
  );
};

// Wrapper component that provides the BlockchainStateContext
const Dapp = () => {
  return (
    <BlockchainStateProvider>
      <DappInner />
    </BlockchainStateProvider>
  );
};

export default Dapp;